import { router } from '@client/components/_core/providers/TanstackRouterProvider';
import { getConfig } from '@client/config/runtime';
import { redactAccessKeysDeep } from '@client/utils/telemetry/access-key-redaction';
import { protectReplayTransport } from '@client/utils/telemetry/recording-privacy';
import * as Sentry from '@sentry/react';
import { E2E_TRAFFIC_TAG, E2E_TRAFFIC_TAG_VALUE, hasChecklyUserAgentToken } from '@shared/utils/core/e2e-traffic';
import { filterSentryEvent } from './sentry-event-filters';
import { withLcpAfterTtfbMeasurement } from './sentry-transaction-measurements';
import { applyTrpcClientErrorGrouping } from './sentry-trpc-grouping';

// Define the URL you want to ignore
const ignoredUrls = ['https://r.lrkt-in.com'];

/** Name fragments the v10 SDK withheld with `sendDefaultPii` unset (the IP-bearing ones). */
const IP_BEARING_FRAGMENTS = ['forwarded', '-ip', 'remote-', 'via', '-user'];

/**
 * The only request headers v10 sent: `httpContextIntegration` added the browser's User-Agent and
 * Referer, and `httpClientIntegration` sent no headers with `sendDefaultPii` unset. Sentry 11 has
 * both read `httpHeaders.request`, so a deny list would ship every other header on a failed
 * request: the device fingerprint, org ID, time zone and the full page URL among them.
 */
const COLLECTED_REQUEST_HEADERS = ['user-agent', 'referer'];

if (
    getConfig('ENV', import.meta.env.VITE_ENV) === 'production' ||
    getConfig('ENV', import.meta.env.VITE_ENV) === 'staging'
) {
    Sentry.init({
        transport: (options) => protectReplayTransport(Sentry.makeFetchTransport(options)),
        // Sentry 11 replaced `sendDefaultPii` with `dataCollection`, and leaving it unset now
        // collects cookies, request and response bodies and the user's IP. Every category is
        // stated; this is what the v10 SDK collected with `sendDefaultPii` unset.
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: { request: { allow: COLLECTED_REQUEST_HEADERS }, response: false },
            httpBodies: [],
            urlQueryParams: { deny: IP_BEARING_FRAGMENTS },
            graphQL: { document: false, variables: false },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            queues: false,
            frameContextLines: 7,
        },
        // Sentry 11 streams spans by default, and under streaming `beforeSendTransaction` is
        // never called, so the access-key redaction and the LCP measurement below would stop
        // silently. The transaction-based lifecycle keeps them running until they are ported
        // to `beforeSendSpan`. Upstream removes `static` in the next major.
        traceLifecycle: 'static',
        // Sentry 11 turned this on by default; it would attach a synthetic stack to every
        // `captureMessage` and regroup those issues.
        attachStacktrace: false,
        dsn: 'https://f9fdd54d90cbb7d44fea170d0f44c6e8@o4508222925242368.ingest.us.sentry.io/4508285831217152',
        ignoreErrors: [
            'Non-Error promise rejection captured with value:',
            'AbortError',
            // tRPC wraps a cancelled request (navigation away mid-query) in a TRPCClientError whose
            // message no longer names AbortError, so the entry above does not match it.
            'signal is aborted without reason',
            'Fetch is aborted',
            'Session expired',
        ],
        // Public access-key URLs are bearer credentials (driver status link, invoice view,
        // invite secret). Strip them from every event, transaction and breadcrumb before it
        // leaves the browser — request.url, navigation breadcrumbs, span data, exception text.
        // Filtering runs first so a dropped event costs nothing. A tRPC error then gets an issue
        // per procedure and code instead of one catch-all (CURIUM-FE-TV).
        beforeSend: (event, hint) => {
            const kept = filterSentryEvent(event);
            return kept === null ? null : redactAccessKeysDeep(applyTrpcClientErrorGrouping(kept, hint));
        },
        beforeSendTransaction: (event) => redactAccessKeysDeep(withLcpAfterTtfbMeasurement(event)),
        beforeBreadcrumb: (breadcrumb) => redactAccessKeysDeep(breadcrumb),
        integrations: [
            // Exactly one BrowserTracing integration. The SDK keys integrations by name and keeps
            // the LAST one registered, so a plain `browserTracingIntegration({...})` alongside the
            // TanStack one used to be discarded together with these options — the request filter
            // never applied (CUR-1053, task 0.3).
            Sentry.tanstackRouterBrowserTracingIntegration(router, {
                shouldCreateSpanForRequest: (url) => {
                    // Check if the URL starts with any of the ignored URLs
                    return !ignoredUrls.some((ignored) => url.startsWith(ignored));
                },
            }),
            Sentry.httpClientIntegration(),
            Sentry.extraErrorDataIntegration(),
            Sentry.httpContextIntegration(),
            Sentry.moduleMetadataIntegration(),
            Sentry.functionToStringIntegration(),
            Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
            Sentry.contextLinesIntegration(),
            // Sentry 11 moved console breadcrumbs to the default `consoleIntegration`.
            Sentry.breadcrumbsIntegration({
                fetch: true,
                history: true,
                sentry: true,
                dom: true,
                xhr: true,
            }),
        ],
        tracesSampleRate: 0.25,
        // Deployment identity (preflight vs staging) when the worker injects it; ENV otherwise.
        environment:
            getConfig('DEPLOY_ENV', import.meta.env.VITE_DEPLOY_ENV ?? '') ||
            getConfig('ENV', import.meta.env.VITE_ENV),

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.3,
        replaysOnErrorSampleRate: 1.0,
    });

    // Defer heavy integrations (replay + profiling) to a separate chunk loaded on idle
    const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
    // The promise is returned for tests; the idle scheduler ignores it.
    idle(() =>
        import('./sentry-deferred').then(
            ({ addDeferredIntegrations }) => addDeferredIntegrations(),
            // Only the chunk load is optional: a content blocker that matches "sentry" in the chunk
            // URL refuses it. Left unhandled, the rejection reaches the stale-chunk handler in
            // index.html, which reloads the page the person is filling in. An error thrown while
            // adding the integrations is not handled here, so Sentry still reports it.
            (error: unknown) => console.warn('[Sentry] Replay and profiling could not load', error),
        ),
    );

    console.log('Sentry initialized');
    console.log('environment:', getConfig('ENV', import.meta.env.VITE_ENV));
    Sentry.setTag('in-maintenance-mode', false);
    // Checkly runs only reach staging and preflight. The tag routes their errors away from the
    // paging alert and filters nothing (CUR-1111).
    if (getConfig('ENV', import.meta.env.VITE_ENV) === 'staging' && hasChecklyUserAgentToken(navigator.userAgent)) {
        Sentry.setTag(E2E_TRAFFIC_TAG, E2E_TRAFFIC_TAG_VALUE);
    }
}
